// Comms Book: a single in-app place for internal notices (replacing the
// shared spreadsheet Taxis Gold & Black used to run), split by audience
// (which roster groups can see a message) and by category (Vehicles,
// Drivers, TaxiCaller, Bookings, Accounts, or any admin-added category).
// See supabase/migrations/*_comms_book*.sql for the schema and RLS --
// visibility and posting rights are enforced there, not just in this UI
// layer.
//
// Audience semantics: null/empty audience_groups means Dispatch/Admin
// ONLY (the safe default) -- broad visibility is something you opt into
// by picking the "Everyone" toggle in the composer (stored as the literal
// string 'Everyone' in audience_groups, not a real roster_driver_groups
// row), or by picking specific real groups.
import { supabase } from "@/lib/supabase";

// This DMS instance is single-tenant -- same fixed-tenant convention used
// throughout the app (incidentReports.ts, taxicaller_full_webhook, etc.).
// Required here specifically because comms_messages.tenant_id has no DB
// default -- an insert that omits it would land as NULL and the RLS
// policy's tenant match would then reject the insert outright.
const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

export interface CommsCategory {
  id: string;
  name: string;
  color_hex: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface RosterGroupOption {
  id: string;
  name: string;
  colour_hex: string | null;
}

export interface CommsAttachment {
  id: string;
  storage_path: string;
}

export interface CommsRead {
  driver_name: string;
  read_at: string;
}

export interface CommsMessage {
  id: string;
  category_id: string | null;
  audience_groups: string[] | null;
  title: string | null;
  body: string;
  is_important: boolean;
  posted_by: string;
  created_at: string;
  archived_at: string | null;
  status: "pending" | "approved" | "rejected";
  comms_categories: { name: string; color_hex: string | null } | null;
  comms_message_attachments: CommsAttachment[];
  comms_message_reads: CommsRead[];
}

const ATTACHMENT_BUCKET = "comms-book";

export async function loadCommsCategories(): Promise<CommsCategory[]> {
  const { data, error } = await supabase
    .from("comms_categories")
    .select("id, name, color_hex, sort_order, is_active")
    .eq("is_active", true)
    .order("sort_order");
  if (error) {
    console.error("Error loading comms categories:", error);
    return [];
  }
  return data || [];
}

/** Includes inactive categories -- for the management screen only. */
export async function loadAllCommsCategories(): Promise<CommsCategory[]> {
  const { data, error } = await supabase.from("comms_categories").select("id, name, color_hex, sort_order, is_active").order("sort_order");
  if (error) {
    console.error("Error loading all comms categories:", error);
    return [];
  }
  return data || [];
}

export async function createCommsCategory(name: string, colorHex: string | null, sortOrder: number): Promise<boolean> {
  const { error } = await supabase
    .from("comms_categories")
    .insert({ tenant_id: DEFAULT_TENANT_ID, name, color_hex: colorHex, sort_order: sortOrder });
  if (error) {
    console.error("Error creating comms category:", error);
    return false;
  }
  return true;
}

export async function updateCommsCategory(
  id: string,
  patch: Partial<Pick<CommsCategory, "name" | "color_hex" | "sort_order" | "is_active">>,
): Promise<boolean> {
  const { error } = await supabase.from("comms_categories").update(patch).eq("id", id);
  if (error) {
    console.error("Error updating comms category:", error);
    return false;
  }
  return true;
}

export async function loadRosterGroupOptions(): Promise<RosterGroupOption[]> {
  const { data, error } = await supabase
    .from("roster_driver_groups")
    .select("id, name, colour_hex")
    .eq("is_active", true)
    .order("sort_order");
  if (error) {
    console.error("Error loading roster groups:", error);
    return [];
  }
  return data || [];
}

const MESSAGE_SELECT =
  "id, category_id, audience_groups, title, body, is_important, posted_by, created_at, archived_at, status, " +
  "comms_categories(name, color_hex), comms_message_attachments(id, storage_path), comms_message_reads(driver_name, read_at)";

/**
 * Always scoped to status='approved' -- Dispatch/Admin's RLS would let
 * them see pending/rejected rows too, but those belong in the separate
 * pending-approval queue (loadPendingCommsMessages), not mixed into the
 * main feed.
 *
 * includeArchived controls whether archived messages appear at all --
 * the "Show All" toggle -- independently of search, so a search always
 * respects it rather than silently bypassing it (archived messages are
 * still fully searchable, just only once "Show All" is switched on).
 */
export async function loadCommsMessages(options?: { includeArchived?: boolean; search?: string }): Promise<CommsMessage[]> {
  let query = supabase
    .from("comms_messages")
    .select(MESSAGE_SELECT)
    .eq("status", "approved")
    .order("is_important", { ascending: false })
    .order("created_at", { ascending: false });

  if (!options?.includeArchived) {
    query = query.is("archived_at", null);
  }
  if (options?.search?.trim()) {
    const term = options.search.trim();
    query = query.or(`title.ilike.%${term}%,body.ilike.%${term}%`);
  }

  const { data, error } = await query;
  if (error) {
    console.error("Error loading comms messages:", error);
    return [];
  }
  return (data as unknown as CommsMessage[]) || [];
}

/** Dispatch/Admin only (RLS returns nothing for anyone else). */
export async function loadPendingCommsMessages(): Promise<CommsMessage[]> {
  const { data, error } = await supabase
    .from("comms_messages")
    .select(MESSAGE_SELECT)
    .eq("status", "pending")
    .order("created_at", { ascending: true });
  if (error) {
    console.error("Error loading pending comms messages:", error);
    return [];
  }
  return (data as unknown as CommsMessage[]) || [];
}

/** A driver's own submissions, any status -- so they can see whether
 *  something they posted is pending/approved/rejected instead of
 *  wondering if it silently failed to send. */
export async function loadMyCommsMessages(postedBy: string): Promise<CommsMessage[]> {
  const { data, error } = await supabase
    .from("comms_messages")
    .select(MESSAGE_SELECT)
    .eq("posted_by", postedBy)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("Error loading my comms messages:", error);
    return [];
  }
  return (data as unknown as CommsMessage[]) || [];
}

/** A driver's own submission -- always lands pending, no audience choice
 *  (Dispatch/Admin set that on approval), no importance flag either. */
export async function submitDriverCommsMessage(input: {
  categoryId: string | null;
  title: string | null;
  body: string;
  postedBy: string;
}): Promise<string | null> {
  const { data, error } = await supabase
    .from("comms_messages")
    .insert({
      tenant_id: DEFAULT_TENANT_ID,
      category_id: input.categoryId,
      audience_groups: null,
      title: input.title || null,
      body: input.body,
      is_important: false,
      posted_by: input.postedBy,
      status: "pending",
    })
    .select("id")
    .single();
  if (error) {
    console.error("Error submitting comms message:", error);
    return null;
  }
  return data.id;
}

export async function approveCommsMessage(id: string, audienceGroups: string[] | null, isImportant: boolean): Promise<boolean> {
  const { error } = await supabase
    .from("comms_messages")
    .update({ status: "approved", audience_groups: audienceGroups && audienceGroups.length > 0 ? audienceGroups : null, is_important: isImportant })
    .eq("id", id);
  if (error) {
    console.error("Error approving comms message:", error);
    return false;
  }
  return true;
}

export async function rejectCommsMessage(id: string): Promise<boolean> {
  const { error } = await supabase.from("comms_messages").update({ status: "rejected" }).eq("id", id);
  if (error) {
    console.error("Error rejecting comms message:", error);
    return false;
  }
  return true;
}

export async function createCommsMessage(input: {
  categoryId: string | null;
  audienceGroups: string[] | null;
  title: string | null;
  body: string;
  isImportant: boolean;
  postedBy: string;
}): Promise<string | null> {
  const { data, error } = await supabase
    .from("comms_messages")
    .insert({
      tenant_id: DEFAULT_TENANT_ID,
      category_id: input.categoryId,
      audience_groups: input.audienceGroups && input.audienceGroups.length > 0 ? input.audienceGroups : null,
      title: input.title || null,
      body: input.body,
      is_important: input.isImportant,
      posted_by: input.postedBy,
    })
    .select("id")
    .single();
  if (error) {
    console.error("Error creating comms message:", error);
    return null;
  }
  return data.id;
}

/** Dispatch/Admin editing any message -- full fields, status untouched
 *  (an admin's own edit doesn't need re-approval). */
export async function updateCommsMessage(
  id: string,
  input: {
    categoryId: string | null;
    audienceGroups: string[] | null;
    title: string | null;
    body: string;
    isImportant: boolean;
  },
): Promise<boolean> {
  const { error } = await supabase
    .from("comms_messages")
    .update({
      category_id: input.categoryId,
      audience_groups: input.audienceGroups && input.audienceGroups.length > 0 ? input.audienceGroups : null,
      title: input.title || null,
      body: input.body,
      is_important: input.isImportant,
    })
    .eq("id", id);
  if (error) {
    console.error("Error updating comms message:", error);
    return false;
  }
  return true;
}

/** A driver editing their own message (any prior status) -- always resets
 *  it back to pending and clears audience/importance so Dispatch/Admin
 *  re-review the new content before anyone else sees it; the DB's own
 *  comms_messages_update_own_pending RLS policy enforces this too, not
 *  just this function. */
export async function updateMyCommsMessage(
  id: string,
  input: { categoryId: string | null; title: string | null; body: string },
): Promise<boolean> {
  const { error } = await supabase
    .from("comms_messages")
    .update({
      category_id: input.categoryId,
      title: input.title || null,
      body: input.body,
      status: "pending",
      audience_groups: null,
      is_important: false,
    })
    .eq("id", id);
  if (error) {
    console.error("Error updating my comms message:", error);
    return false;
  }
  return true;
}

export async function deleteCommsMessage(id: string): Promise<boolean> {
  const { error } = await supabase.from("comms_messages").delete().eq("id", id);
  if (error) {
    console.error("Error deleting comms message:", error);
    return false;
  }
  return true;
}

export async function uploadCommsAttachment(messageId: string, file: File): Promise<boolean> {
  const path = `messages/${messageId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const { error: uploadError } = await supabase.storage.from(ATTACHMENT_BUCKET).upload(path, file, {
    contentType: file.type || "image/png",
    upsert: false,
  });
  if (uploadError) {
    console.error("Error uploading comms attachment:", uploadError);
    return false;
  }
  const { error: insertError } = await supabase.from("comms_message_attachments").insert({ message_id: messageId, storage_path: path });
  if (insertError) {
    console.error("Error recording comms attachment:", insertError);
    return false;
  }
  return true;
}

export async function deleteCommsAttachment(attachmentId: string, storagePath: string): Promise<void> {
  await supabase.from("comms_message_attachments").delete().eq("id", attachmentId);
  await supabase.storage.from(ATTACHMENT_BUCKET).remove([storagePath]);
}

export function commsAttachmentUrl(path: string): string {
  return supabase.storage.from(ATTACHMENT_BUCKET).getPublicUrl(path).data.publicUrl;
}

/** Idempotent -- re-ticking an already-read message is a no-op, not an error. */
export async function markCommsMessageRead(messageId: string, driverName: string): Promise<boolean> {
  const { error } = await supabase
    .from("comms_message_reads")
    .upsert({ message_id: messageId, driver_name: driverName }, { onConflict: "message_id,driver_name", ignoreDuplicates: true });
  if (error) {
    console.error("Error recording comms message read:", error);
    return false;
  }
  return true;
}

/**
 * Unread count for the Comms Book tab badge on the driver dashboard --
 * "unread" = an approved, non-archived message this driver can see with no
 * comms_message_reads row for them yet. Mirrors CommsBook.tsx's own
 * isDispatchOrAdmin/isVisibleToRosterGroups split exactly (a Dispatch/Admin
 * driver sees every message RLS returns; anyone else only "Everyone" or
 * their own roster group's), so the badge count and what the tab actually
 * shows never disagree.
 */
export async function countUnreadCommsMessages(
  driverName: string,
  isDispatchOrAdminDriver: boolean,
  driverRosterGroups?: string[] | null,
): Promise<number> {
  if (!driverName) return 0;
  const messages = await loadCommsMessages();
  const groups = driverRosterGroups || [];
  return messages.filter((m) => {
    if (!isDispatchOrAdminDriver) {
      if (!m.audience_groups || m.audience_groups.length === 0) return false; // Dispatch/Admin only
      if (!m.audience_groups.includes("Everyone") && !m.audience_groups.some((g) => groups.includes(g))) return false;
    }
    return !m.comms_message_reads.some((r) => r.driver_name === driverName);
  }).length;
}

export function isDispatchOrAdmin(driver: { isAdmin?: boolean; roster_groups?: string[] | null } | null | undefined): boolean {
  if (!driver) return false;
  return !!driver.isAdmin || !!driver.roster_groups?.includes("Dispatch");
}
